import { register } from "api/auth";
import { ApiError } from "api/client";
import { Form, Formik, type FormikHelpers } from "formik";
import AccountsContainer from "pages/accounts-onboarding/components/accounts-container";
import type { RegisterFormModel } from "pages/accounts-onboarding/models";
import { createRegisterFormSchema } from "pages/accounts-onboarding/schema";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { getPostSignInPath } from "routes/signInRedirect";
import { useSession } from "Session";
import { clearAcquisitionMetadata, formatPersonName, readAcquisitionMetadata } from "utilities";

import RegisterForm from "./components";

type RegisterPageProps = {
	initialValues: RegisterFormModel;
};

export const RegisterPage = ({ initialValues }: RegisterPageProps) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { signIn } = useSession();
	const { t } = useTranslation("accountsPage");
	const validationSchema = useMemo(() => createRegisterFormSchema(t), [t]);

	const onFormSubmit = useCallback(
		async (values: RegisterFormModel, formikHelpers: FormikHelpers<RegisterFormModel>): Promise<void> => {
			try {
				// Backend creates the account, emails a verification link, and returns an authenticated session.
				const auth = await register({
					// The ticked consent box. The API records it against the new account, so it has to
					// travel with the registration rather than being a client-side gate only.
					acceptedTerms: values.acceptedTerms,
					// Which link brought them here, captured on their first visit — possibly days ago.
					acquisition: readAcquisitionMetadata(),
					email: values.email,
					// The API stores the canonical "Firstname M Lastname" form; send it so the name the
					// user sees after signing in matches what they just typed.
					fullName: formatPersonName(values.fullName),
					password: values.password,
					// Solved by the Turnstile widget. Empty when no site key is configured, which the API
					// reads as "not checked" rather than as a failed challenge.
					turnstileToken: values.turnstileToken,
					// Empty for every real submission — only a bot fills the hidden honeypot field.
					website: values.website
				});
				// Cleared only after the account exists, so a failed attempt can be retried with the
				// attribution intact — and so a second account on this device does not inherit it.
				clearAcquisitionMetadata();
				signIn(auth);
				navigate(getPostSignInPath(location.search), { replace: true });
			} catch (error) {
				formikHelpers.setStatus(error instanceof ApiError ? error.message : t("register.error"));
				formikHelpers.setSubmitting(false);
			}
		},
		[location.search, navigate, signIn, t]
	);

	return (
		<Formik initialValues={initialValues} onSubmit={onFormSubmit} validationSchema={validationSchema}>
			<Form noValidate>
				<AccountsContainer>
					<RegisterForm />
				</AccountsContainer>
			</Form>
		</Formik>
	);
};
